import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import FadeContent from '../components/FadeContent';
import { 
  Printer, 
  Award, 
  Clock, 
  Users, 
  Star, 
  ArrowRight, 
  FileText,
  Palette,
  BookOpen,
  Phone,
  MessageCircle,
  CheckCircle2,
  Sparkles,
  Coffee,
  ShoppingBag,
  Shirt,
  Gift,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

const Home = () => {

  const showOctoberBanner = true;
  const partnerTrackRef = useRef<HTMLDivElement>(null);

  const scrollPartners = (direction: 'left' | 'right') => {
    const container = partnerTrackRef.current;
    if (!container) return;

    const firstItem = container.querySelector<HTMLDivElement>('.partner-item');
    const computedStyles = container ? window.getComputedStyle(container) : null;
    const gap = computedStyles ? parseFloat(computedStyles.columnGap || computedStyles.gap || '0') : 0;
    const itemWidth = firstItem ? firstItem.clientWidth + gap : container.clientWidth / 3;
    const scrollAmount = itemWidth * 2;

    container.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth'
    });
  };

  const octoberProducts = [
    {
      icon: <Coffee className="w-8 h-8" aria-hidden="true" />,
      label: "Mugs solidaires"
    },
    {
      icon: <ShoppingBag className="w-8 h-8" aria-hidden="true" />,
      label: "Tote bags engagés"
    },
    {
      icon: <Shirt className="w-8 h-8" aria-hidden="true" />,
      label: "T-shirts ruban rose"
    },
    {
      icon: <Gift className="w-8 h-8" aria-hidden="true" />,
      label: "Goodies lumineux"
    }
  ];

  const services = [
    {
      icon: <FileText className="w-8 h-8" />,
      title: "Conception",
      description: "Création et développement visuel de vos produits",
      price: "À partir de 15,000 FCFA",
      features: ["Design professionnel", "Conseils créatifs", "Formats optimisés"],
      // SERVICE IMAGE 1 - Remplacez par vos exemples de design/maquettes
      image: "https://images.unsplash.com/photo-1579642984744-4dd0fe83c38c?ixlib=rb-4.1.0&q=80&w=800"
    },
    {
      icon: <Printer className="w-8 h-8" />,
      title: "Production",
      description: "Fabrication et impression de vos produits et articles",
      price: "À partir de 25,000 FCFA",
      features: ["Haute qualité", "Délais respectés", "Contrôle qualité"],
      // SERVICE IMAGE 2 - Remplacez par vos machines/processus de production
      image: "https://images.unsplash.com/photo-1561015314-6bd8c1e875ee?ixlib=rb-4.1.0&q=80&w=800"
    },
    {
      icon: <Palette className="w-8 h-8" />,
      title: "Façonnage",
      description: "Assemblage, finition et mise en forme de vos éléments imprimés",
      price: "À partir de 35,000 FCFA",
      features: ["Finitions premium", "Assemblage précis", "Présentation soignée"],
      // SERVICE IMAGE 3 - Remplacez par vos finitions/reliure/assemblage
      image: "https://images.unsplash.com/photo-1676474506722-4bf98059b74a?ixlib=rb-4.1.0&q=80&w=800"
    }
  ];

  const testimonials = [
    {
      name: "Kouadio Jean-Baptiste",
      company: "Directeur, Entreprise ABC Consulting",
      text: "Service exceptionnel ! DigiPrint a transformé notre image de marque avec des cartes de visite d'une qualité remarquable. L'équipe comprend parfaitement nos besoins professionnels.",
      rating: 5,
      avatar: "👨🏿‍💼"
    },
    {
      name: "Aya Fatou Koné",
      company: "Fondatrice, Boutique Mode Abidjan",
      text: "Très satisfaite des flyers pour ma boutique ! La créativité et le professionnalisme de l'équipe ont dépassé mes attentes. Mes clients adorent le design.",
      rating: 5,
      avatar: "👩🏿‍💼"
    },
    {
      name: "Dr. Bamba Moussa",
      company: "Propriétaire, Restaurant Le Palmier",
      text: "Nos menus et supports de communication ont été imprimés avec une qualité exceptionnelle. Le service est rapide et les prix très compétitifs pour Abidjan.",
      rating: 5,
      avatar: "👨🏿‍🍳"
    }
  ];

  const partnerLogos = [
    { name: "PROPARCO", src: "/assets/partners/PROPARCO_Logo_RVB-2048x802.png" },
    { name: "Banque mondiale", src: "/assets/partners/logo_worldbank-e1744098691635.jpg" },
    { name: "PAA", src: "/assets/partners/paa-s.png" },
    { name: "CGRAE", src: "/assets/partners/cgrae-s.png" },
    { name: "EKA", src: "/assets/partners/eka_LG0H_400x400.jpg" },
    { name: "CNAM", src: "/assets/partners/logo_CNAM.png" },
    { name: "ADB", src: "/assets/partners/adb-s.png" },
    { name: "Save the Children", src: "/assets/partners/138_Save_the_Children_color_logo_xUNExA-991x1024.png" },
    { name: "PROPARCO Initiative", src: "/assets/partners/WhatsApp-Image-2025-01-10-at-12.08.20.jpeg" },
    { name: "Programme National", src: "/assets/partners/telechargement-2.png" },
    { name: "Partenaire FBK", src: "/assets/partners/fbk3d6ph0246t5d9k86sy1vx47t9.png" }
  ];

  const stats = [
    { number: "2,000+", label: "Impressions par jour", icon: <Printer className="w-6 h-6" /> },
    { number: "500+", label: "Clients satisfaits", icon: <Users className="w-6 h-6" /> },
    { number: "15+", label: "Machines", icon: <Award className="w-6 h-6" /> },
    { number: "10+", label: "Spécialistes", icon: <CheckCircle2 className="w-6 h-6" /> }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-warm-gray-50 to-warm-gray-100">
      {showOctoberBanner && (
        <section className="october-banner" aria-labelledby="october-banner-title">
          <div className="october-banner__glow" aria-hidden="true" />
          <div className="october-banner__gradient" aria-hidden="true" />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="october-banner__layout">
              <FadeContent className="space-y-6">
                <div className="october-banner__eyebrow">
                  Octobre Rose
                </div>
                <div className="space-y-4">
                  <h2 id="october-banner-title" className="october-banner__title">
                    Octobre Rose 🎀 – Collection Spéciale Goodies Solidaires
                  </h2>
                  <p className="october-banner__subtitle">
                    Découvrez nos mugs, tote bags et t-shirts en édition spéciale Octobre Rose. Affichez votre soutien à la lutte contre le cancer du sein.
                  </p>
                </div>
                <div className="october-banner__actions">
                  <Link to="/octobre-rose" className="btn-banner-primary">
                    Voir la collection
                  </Link>
                  <Link to="/contact" className="btn-banner-alt">
                    Commander maintenant
                  </Link>
                </div>
              </FadeContent>
              <FadeContent className="october-banner__showcase">
                <div className="october-banner__ribbon" aria-hidden="true">
                  <svg viewBox="0 0 120 220" xmlns="http://www.w3.org/2000/svg" role="presentation">
                    <path d="M60 10c22 0 40 18 40 40 0 22-18 40-40 80S20 168 20 140c0-22 18-40 40-80-22 0-40-18-40-40S38 10 60 10z" fill="#e91d61" opacity="0.85" />
                    <path d="M60 20c-12 0-22 10-22 22s10 22 22 22 22-10 22-22S72 20 60 20z" fill="#f8a3c1" opacity="0.6" />
                  </svg>
                </div>
                <div className="october-banner__cards">
                  {octoberProducts.map((product, index) => (
                    <div key={product.label} className={`october-banner__card stagger-${index + 1}`}>
                      <div className="october-banner__icon">
                        {product.icon}
                      </div>
                      <p className="october-banner__label">{product.label}</p>
                    </div>
                  ))}
                </div>
              </FadeContent>
            </div>
          </div>
        </section>
      )}
      {/* Hero Section - Digiprint Inspired */}
      <section className="relative hero-modern text-neutral-900 py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary-pink/5 to-transparent" />
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-32 h-32 bg-primary-pink/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-32 right-32 w-48 h-48 bg-primary-pink/15 rounded-full blur-3xl animate-pulse delay-1000" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="asymmetric-grid">
            <FadeContent className="space-y-8">
              <div className="space-y-6">
                <div className="section-tag">
                  <Sparkles className="w-4 h-4 mr-2" />
                  L'art du numérique
                </div>
                <h1 className="text-hero font-bold leading-tight text-neutral-900">
                  L'art du numérique,
                  <span className="block text-primary-pink">
                    à chaque clic
                  </span>
                </h1>
                <p className="text-body-large text-neutral-600 max-w-xl">
                  Imprimerie innovante, créative et audacieuse. Notre expertise en impression numérique 
                  assure des projets uniques et mémorables. Votre succès est notre priorité.
                </p>
              </div>

              <div className="flex flex-wrap gap-4">
                <Link 
                  to="/services" 
                  className="btn-primary inline-flex items-center group"
                >
                  Nos Solutions
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <a 
                  href="https://wa.me/2250787635341" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn-secondary inline-flex items-center group"
                >
                  <MessageCircle className="mr-2 w-5 h-5" />
                  Devis Gratuit
                </a>
              </div>

            </FadeContent>

            <div className="relative">
              <div className="floating-element">
                {/* HERO IMAGE - Remplacez par une photo de vos équipements/atelier */}
                <img 
                  src="https://images.unsplash.com/photo-1693031630146-568e2f72db0e?ixlib=rb-4.1.0&q=80&w=800" 
                  alt="Équipement d'impression numérique moderne DigiPrint Abidjan"
                  className="rounded-3xl shadow-2xl w-full"
                />
                <div className="absolute -bottom-6 -right-6 bg-primary-pink text-white p-6 rounded-2xl shadow-xl">
                  <div className="text-2xl font-bold">24h</div>
                  <div className="text-sm">Livraison</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Full-width Statistics Section */}
        <div className="w-full bg-gradient-to-r from-primary-pink/5 via-primary-pink/10 to-primary-pink/5 py-16 mt-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <FadeContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                <div className="text-center flex flex-col items-center justify-center p-6">
                  <div className="text-4xl lg:text-5xl font-bold mb-3" style={{color: '#AD1457'}}>2000+</div>
                  <div className="text-sm lg:text-base text-neutral-700 font-medium">Impressions par jour</div>
                </div>
                <div className="text-center flex flex-col items-center justify-center p-6">
                  <div className="text-4xl lg:text-5xl font-bold mb-3" style={{color: '#AD1457'}}>500+</div>
                  <div className="text-sm lg:text-base text-neutral-700 font-medium">Clients satisfaits</div>
                </div>
                <div className="text-center flex flex-col items-center justify-center p-6">
                  <div className="text-4xl lg:text-5xl font-bold mb-3" style={{color: '#AD1457'}}>15+</div>
                  <div className="text-sm lg:text-base text-neutral-700 font-medium">Machines</div>
                </div>
                <div className="text-center flex flex-col items-center justify-center p-6">
                  <div className="text-4xl lg:text-5xl font-bold mb-3" style={{color: '#AD1457'}}>50+</div>
                  <div className="text-sm lg:text-base text-neutral-700 font-medium">Spécialistes</div>
                </div>
              </div>
            </FadeContent>
          </div>
        </div>
      </section>

      {/* Notre Expertise Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="section-tag mx-auto">
              Notre Expertise
            </div>
            <h2 className="text-display font-bold text-neutral-900 mb-6">
              Impression numérique
            </h2>
            <p className="text-body-large text-neutral-600 max-w-3xl mx-auto">
              Notre équipe hautement qualifiée vous accompagne dans tous vos projets d'impression,
              de la conception à la finition, avec un service personnalisé et des résultats exceptionnels.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <FadeContent 
                key={index} 
                className={`card-modern hover:shadow-xl transition-all duration-300 stagger-${index + 1}`}
              >
                <div className="service-icon mx-auto">
                  {service.icon}
                </div>
                <h3 className="text-headline font-bold text-neutral-900 mb-4 text-center">
                  {service.title}
                </h3>
                <p className="text-body text-neutral-600 mb-6 text-center">
                  {service.description}
                </p>
                <ul className="space-y-2 mb-6">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center text-sm text-neutral-600">
                      <CheckCircle2 className="w-4 h-4 text-primary-pink mr-2 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </FadeContent>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-warm-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="asymmetric-grid-reverse">
            <div className="relative">
              {/* ABOUT IMAGE - Remplacez par une photo de votre équipe au travail */}
              <img 
                src="https://images.unsplash.com/photo-1560472354-b33ff0c44a43?ixlib=rb-4.1.0&q=80&w=800" 
                alt="Équipe DigiPrint Abidjan au travail"
                className="rounded-3xl shadow-2xl w-full"
              />
            </div>
            
            <FadeContent className="space-y-6">
              <div className="section-tag">
                Notre Expertise
              </div>
              <h2 className="text-display font-bold text-neutral-900">
                Une équipe hautement qualifiée
              </h2>
              <p className="text-body-large text-neutral-600">
                Nous sommes fiers de notre équipe de production, qui comprend plus de 10 spécialistes 
                et de nos ensembles d'équipements de pointe, notamment des machines de façonnage, 
                des plieuses et trieuses de haute précision.
              </p>
              <p className="text-body text-neutral-600">
                Cette expertise nous permet de garantir des résultats de la plus haute qualité 
                pour chaque projet que nous entreprenons.
              </p>
              <Link 
                to="/about" 
                className="btn-secondary inline-flex items-center group"
              >
                À propos de DigiPrint
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </FadeContent>
          </div>
        </div>
      </section>

      {/* Compact Partners Section */}
      <section className="partners-section" aria-labelledby="partners-title">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeContent className="mb-6 text-center">
            <h2
              id="partners-title"
              className="tracking-[0.4em] text-xs sm:text-sm font-semibold text-neutral-500 uppercase"
            >
              ILS NOUS FONT CONFIANCE
            </h2>
          </FadeContent>
          <FadeContent>
            <div className="partners-carousel">
              <button
                type="button"
                className="partners-button"
                onClick={() => scrollPartners('left')}
                aria-label="Défiler les logos vers la gauche"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="partners-window">
                <div className="partners-track" ref={partnerTrackRef}>
                  {partnerLogos.map((logo) => (
                    <div key={logo.name} className="partner-item">
                      <img
                        src={logo.src}
                        alt={logo.name}
                        loading="lazy"
                        className="partner-logo"
                      />
                    </div>
                  ))}
                </div>
              </div>
              <button
                type="button"
                className="partners-button"
                onClick={() => scrollPartners('right')}
                aria-label="Défiler les logos vers la droite"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </FadeContent>
        </div>
      </section>

      {/* Services Preview Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="section-tag mx-auto">
              Commencez dès aujourd'hui
            </div>
            <h2 className="text-display font-bold text-neutral-900 mb-6">
              Explorez notre gamme de services
            </h2>
            <Link 
              to="/services" 
              className="btn-secondary inline-flex items-center group"
            >
              Voir toutes nos solutions
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { title: "Sérigraphie", desc: "Technique d'impression directe utilisée pour le textile, le cuir et plus." },
              { title: "Impression", desc: "Toute la papeterie d'entreprise : cartes de visite, brochures, chemises, enveloppes…" },
              { title: "Gadgets", desc: "Goodies personnalisés et marqués pour laisser une impression durable." },
              { title: "Packaging", desc: "Emballages créatifs, fonctionnels et personnalisés sur mesure." },
              { title: "Affichage", desc: "Impressions durables, impactantes et visibles en grand." }
            ].map((service, index) => (
              <FadeContent 
                key={index} 
                className={`card-modern group cursor-pointer stagger-${index + 1}`}
              >
                <h3 className="text-headline font-bold text-neutral-900 mb-4 group-hover:text-primary-pink transition-colors">
                  {service.title}
                </h3>
                <p className="text-body text-neutral-600">
                  {service.desc}
                </p>
              </FadeContent>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-warm-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="section-tag mx-auto">
              Ils nous font confiance
            </div>
            <h2 className="text-display font-bold text-neutral-900">
              Ce que disent nos clients
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <FadeContent 
                key={index} 
                className={`card-modern stagger-${index + 1}`}
              >
                <div className="flex items-center mb-4">
                  <div className="text-3xl mr-3">{testimonial.avatar}</div>
                  <div>
                    <h4 className="font-semibold text-neutral-900">{testimonial.name}</h4>
                    <p className="text-sm text-neutral-600">{testimonial.company}</p>
                  </div>
                </div>
                <div className="testimonial-quote">
                  {testimonial.text}
                </div>
                <div className="flex text-primary-pink mt-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-current" />
                  ))}
                </div>
              </FadeContent>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 hero-accent text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <FadeContent className="space-y-8">
            <h2 className="text-display font-bold">
              Prêt à donner vie à vos projets ?
            </h2>
            <p className="text-body-large max-w-2xl mx-auto opacity-90">
              Contactez-nous dès maintenant pour un devis personnalisé et découvrez 
              comment notre expertise peut transformer vos idées en réalisations exceptionnelles.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="https://wa.me/2250787635341" 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn-accent bg-white text-primary-pink hover:bg-neutral-100 inline-flex items-center"
              >
                <MessageCircle className="mr-2 w-5 h-5" />
                WhatsApp
              </a>
              <Link 
                to="/contact" 
                className="btn-secondary border-white text-white hover:bg-white hover:text-primary-pink inline-flex items-center"
              >
                <Phone className="mr-2 w-5 h-5" />
                Nous contacter
              </Link>
            </div>
          </FadeContent>
        </div>
      </section>
    </div>
  );
};

export default Home;